import { authService } from './auth.service';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export interface Document {
  id: string;
  project_id: string;
  project_name?: string;
  name: string;
  type: 'plano' | 'contrato' | 'permiso' | 'factura' | 'reporte' | 'otro';
  category: string;
  url: string;
  thumbnail_url?: string;
  size: number;
  uploaded_by: string;
  uploaded_by_name?: string;
  signed: boolean;
  signed_at?: number;
  signed_by?: string;
  requires_signature?: number; 
  created_at: number;
  updated_at: number;
  metadata?: Record<string, any>;
  download_url?: string;
  download_url_expires_at?: number;
}

export interface CreateDocumentData {
  project_id: string;
  name: string;
  type: Document['type'];
  category: string;
  file: File;
}

export interface UpdateDocumentData {
  name?: string;
  type?: Document['type'];
  category?: string;
  signed?: boolean;
}

interface PresignUploadResponse {
  upload_url: string;
  public_url: string;
  key: string;
  expires_at: number;
}

export const documentService = {
  // ============================================================
  // OBTENER DOCUMENTOS
  // ============================================================

  async getDocuments(projectId?: string): Promise<Document[]> {
    const url = projectId
      ? `${API_URL}/api/documents?project_id=${projectId}`
      : `${API_URL}/api/documents`;
    const response = await authService.fetchWithAuth(url);
    return response.data || [];
  },

  async getDocument(id: string): Promise<Document> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/documents/${id}`);
    return response.data;
  },

  // ============================================================
  // CREAR DOCUMENTO (flujo con Presigned URL)
  // ============================================================

  async createDocument(data: CreateDocumentData): Promise<Document> {
    // 1. Comprimir la imagen si aplica
    let fileToUpload = data.file;
    if (data.file.type.startsWith('image/')) {
      fileToUpload = await this.compressImage(data.file);
    }

    // 2. Pedir Presigned URL al Worker
    const presignResponse = await authService.fetchWithAuth(
      `${API_URL}/api/documents/presign-upload`,
      {
        method: 'POST',
        body: JSON.stringify({
          project_id: data.project_id,
          file_name: fileToUpload.name,
          file_type: fileToUpload.type || 'application/octet-stream',
          file_size: fileToUpload.size,
        }),
      }
    );

    const presign: PresignUploadResponse = presignResponse.data;

    // 3. Subir el archivo directo a R2 con la URL firmada (PUT)
    const uploadResponse = await fetch(presign.upload_url, {
      method: 'PUT',
      headers: {
        'Content-Type': fileToUpload.type || 'application/octet-stream',
      },
      body: fileToUpload,
    });

    if (!uploadResponse.ok) {
      throw new Error(`Error al subir el archivo a R2: ${uploadResponse.statusText}`);
    }

    // 4. Registrar el documento en D1
    const response = await authService.fetchWithAuth(`${API_URL}/api/documents`, {
      method: 'POST',
      body: JSON.stringify({
        project_id: data.project_id,
        name: data.name,
        type: data.type,
        category: data.category,
        url: presign.public_url,
        size: fileToUpload.size,
        r2_key: presign.key,
      }),
    });

    return response.data;
  },

  async updateDocument(id: string, data: UpdateDocumentData): Promise<Document> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/documents/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  async deleteDocument(id: string): Promise<void> {
    await authService.fetchWithAuth(`${API_URL}/api/documents/${id}`, {
      method: 'DELETE',
    });
  },

  // ============================================================
  // FIRMA DE DOCUMENTOS
  // ============================================================

  async signDocument(id: string): Promise<Document> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/documents/${id}/sign`, {
      method: 'POST',
    });
    return response.data;
  },

  // ============================================================
  // OBTENER URL DE DESCARGA FIRMADA
  // ============================================================

  async getDownloadUrl(id: string): Promise<{ url: string; expiresAt: number }> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/documents/${id}`);
    const doc: Document = response.data;

    if (doc.download_url && doc.download_url_expires_at) {
      return { url: doc.download_url, expiresAt: doc.download_url_expires_at };
    }

    return { url: doc.url, expiresAt: 0 };
  },

  // ============================================================
  // COMPRESIÓN DE IMÁGENES
  // ============================================================

  async compressImage(file: File, maxWidth = 1200, maxHeight = 1200, quality = 0.85): Promise<File> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('No se pudo crear el contexto de canvas'));
            return;
          }

          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('No se pudo comprimir la imagen'));
                return;
              }
              const compressedFile = new File(
                [blob],
                file.name.replace(/\.[^.]+$/, '.webp'),
                { type: 'image/webp' }
              );
              resolve(compressedFile);
            },
            'image/webp',
            quality
          );
        };
        img.onerror = () => reject(new Error('Error al cargar la imagen'));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error('Error al leer el archivo'));
      reader.readAsDataURL(file);
    });
  },

  // ============================================================
  // ESTADÍSTICAS
  // ============================================================

  async getDocumentStats(projectId?: string): Promise<{
    total: number;
    by_type: Record<string, number>;
    signed: number;
    unsigned: number;
    total_size: number;
  }> {
    const url = projectId
      ? `${API_URL}/api/documents/stats?project_id=${projectId}`
      : `${API_URL}/api/documents/stats`;
    const response = await authService.fetchWithAuth(url);
    return response.data;
  },
};